import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ContactInput, Contact } from '../types';
import { apiService, buildApiUrl } from '../services/api';
import { validatePhone, normalizePhoneInput } from '../utils/phoneUtils';
import { maskCPF } from '../utils/documentUtils';

interface Category {
  id: string;
  nome: string;
}

const contactSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  document: z
    .string()
    .optional()
    .refine(
      (value) => !value || value.replace(/\D/g, '').length === 11,
      'CPF deve ter 11 dígitos'
    ),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, {
    message: 'Formato de telefone inválido',
  }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  bairro: z.string().optional(),
  cidade: z.string().optional(),
  observacoes: z.string().optional(),
  categoriaId: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ContactForm({ contact, onSuccess, onCancel }: ContactFormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: contact?.nome || '',
      telefone: contact?.telefone || '+55 ',
      email: contact?.email || '',
      bairro: contact?.bairro || '',
      cidade: contact?.cidade || '',
      document: contact?.document || '',
      observacoes: contact?.observacoes || '',
      categoriaId: contact?.categoriaId || '',
    },
  });

  const documentValue = watch('document');
  const phoneValue = watch('telefone');

  useEffect(() => {
    const masked = maskCPF(documentValue || '');
    if (masked !== (documentValue || '')) {
      setValue('document', masked);
    }
  }, [documentValue, setValue]);

  useEffect(() => {
    const normalized = normalizePhoneInput(phoneValue || '');
    if (normalized !== (phoneValue || '')) {
      setValue('telefone', normalized);
    }
  }, [phoneValue, setValue]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(buildApiUrl('/categorias'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      const cleanDocument = (data.document || '').replace(/\D/g, '');
      const cleanPhone = (data.telefone || '').replace(/\D/g, '');

      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: cleanPhone,
        document: cleanDocument || undefined,
        email: data.email || undefined,
        bairro: data.bairro || undefined,
        cidade: data.cidade || undefined,
        observacoes: data.observacoes || undefined,
        categoriaId: data.categoriaId || undefined,
      };

      if (contact) {
        await apiService.updateContact(contact.id, contactInput);
        toast.success('Contato atualizado com sucesso');
      } else {
        await apiService.createContact(contactInput);
        toast.success('Contato criado com sucesso');
      }

      onSuccess();
    } catch (err: any) {
      // Verificar se é erro de quota
      if (err?.isQuotaError || err?.upgradeRequired) {
        toast.error(err.message || 'Limite de contatos atingido. Faça upgrade do seu plano para continuar.', {
          duration: 6000,
          icon: '⚠️'
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar contato';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 sm:p-6 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg border border-gray-100 max-h-[calc(100vh-2rem)] overflow-y-auto" role="dialog" aria-labelledby="form-title">
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 id="form-title" className="text-xl sm:text-2xl font-bold text-gray-900">
            {contact ? 'Editar Contato' : 'Novo Contato'}
          </h2>
          <p className="text-gray-500 mt-2">
            {contact ? 'Atualize as informações do contato' : 'Preencha os dados para criar um novo contato'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">
          <div>
            <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
              Nome *
            </label>
            <input
              id="nome"
              type="text"
              {...register('nome')}
              className="input-field"
              placeholder="Digite o nome completo"
            />
            {errors.nome && (
              <p className="text-red-500 text-sm mt-1">
                {errors.nome.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
              CPF
            </label>
            <input
              id="document"
              type="text"
              {...register('document')}
              className="input-field"
              placeholder="Digite o número do CPF"
            />
            {errors.document && (
              <p className="text-red-500 text-sm mt-1">
                {errors.document.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2">
              Telefone *
            </label>
            <input
              id="telefone"
              type="tel"
              {...register('telefone')}
              placeholder="+55 11 99999-9999"
              className="input-field"
            />
            {errors.telefone && (
              <p className="text-red-500 text-sm mt-1">
                {errors.telefone.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              className="input-field"
              placeholder="Digite o email"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="bairro" className="block text-sm font-semibold text-gray-700 mb-2">
                Bairro
              </label>
              <input
                id="bairro"
                type="text"
                {...register('bairro')}
                className="input-field"
                placeholder="Digite o bairro"
              />
            </div>

            <div>
              <label htmlFor="cidade" className="block text-sm font-semibold text-gray-700 mb-2">
                Cidade
              </label>
              <input
                id="cidade"
                type="text"
                {...register('cidade')}
                className="input-field"
                placeholder="Digite a cidade"
              />
            </div>
          </div>


          <div>
            <label htmlFor="categoriaId" className="block text-sm font-semibold text-gray-700 mb-2">
              Categoria
            </label>
            <select
              id="categoriaId"
              {...register('categoriaId')}
              className="input-field"
              disabled={loadingCategories}
            >
              <option value="">Selecione uma categoria</option>
              {loadingCategories ? (
                <option disabled>Carregando categorias...</option>
              ) : (
                categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.nome}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label htmlFor="observacoes" className="block text-sm font-semibold text-gray-700 mb-2">
              Observações
            </label>
            <textarea
              id="observacoes"
              {...register('observacoes')}
              rows={4}
              className="input-field resize-none"
              placeholder="Digite observações adicionais"
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 sm:pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex-1 py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </>
              ) : (
                'Salvar Contato'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
